import { useState, useEffect, useRef, useId } from 'react'
import { X, Globe, Wand2 } from 'lucide-react'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { composeModelSlug, fillModelPath, isDerivedPath, modelPathFieldKeys } from './modelPath'

/**
 * Panneau « Adresse » d'un CONTENU — son URI publique.
 *
 * Le pendant du panneau d'une page, réduit à ce qu'un contenu possède
 * réellement. Une page choisit son chemin entier et son site ; un contenu, non :
 *
 *  - le CHEMIN est celui du modèle de son type (`/actualites/@titre`), réglé
 *    une fois pour tout le type dans l'onglet « Modèle ». Il est montré ici,
 *    en lecture : c'est ce qui rend le segment intelligible.
 *  - le SITE est celui de son type, dont il hérite à la création.
 *  - reste le SEGMENT d'adresse. Il appartient au contenu — mais c'est le
 *    CHEMIN qui dit comment il se compose : un chemin en `@champ` le DÉRIVE
 *    des champs (rien à saisir, il suit le titre), un chemin hérité en `:slug`
 *    le laisse à la main.
 *
 * Rien n'est écrit ici : « Appliquer » pose la valeur dans l'état de l'écran,
 * l'« Enregistrer » de la barre la persiste. L'unicité est tranchée par Drupal
 * au save (suffixe automatique par type) et le serveur renvoie le slug retenu —
 * le front n'en décide pas, il l'affiche.
 */
export const BuilderObjectAddressPanel = ({
  slug,
  onApply,
  onClose,
  modelPath = '',
  data = {},
  modelTitle = '',
  siteName = '',
  titleFallback = '',
  isNew = false,
}) => {
  const scrim = useScrimDismiss(onClose)
  const fieldId = useId()
  const helpId = useId()
  const [draft, setDraft] = useState(slug || '')
  const inputRef = useRef(null)

  // Le chemin du modèle DÉRIVE-t-il le segment des champs du contenu
  // (`/actualites/@titre`) ? Alors il n'y a rien à saisir ici : le segment se
  // recalcule à chaque enregistrement, et un champ de saisie ne ferait que
  // promettre une adresse que le serveur écraserait aussitôt.
  const derived = isDerivedPath(modelPath)
  const derivedSlug = derived ? composeModelSlug(modelPath, data) : ''
  const citedFields = derived ? modelPathFieldKeys(modelPath) : []

  // Le focus va au champ quand il y en a un ; sinon le panneau se lit, et
  // c'est le bouton de fermeture qui prend le focus naturellement.
  useEffect(() => { inputRef.current?.focus() }, [])
  useEscapeClose(onClose)

  // La normalisation est celle du back (`slugify`) : minuscules, tirets. La
  // faire ici évite d'annoncer une adresse que le serveur réécrirait.
  const normalize = (v) => v.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')

  const effective = derived
    ? (derivedSlug || slug)
    : (draft.trim() || (isNew ? '' : slug))
  const preview = modelPath ? fillModelPath(modelPath, effective || 'mon-contenu') : ''

  const apply = () => {
    onApply(normalize(draft.trim()))
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" {...scrim}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="builder-object-address-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-teal-700" aria-hidden="true" />
            <h2 id="builder-object-address-title" className="text-base font-semibold text-gray-800">
              Adresse du contenu
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Fermer">
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="px-6 py-5 space-y-4">
          {derived ? (
            /* Segment DÉRIVÉ : ce n'est pas un champ, c'est le résultat d'une
               règle posée pour tout le type. On le montre, on dit d'où il
               vient, et on laisse aller la corriger là où elle se règle. */
            <div>
              <p className="block text-xs font-medium text-gray-500 mb-1.5">
                Segment d&apos;adresse
              </p>
              <p className="w-full px-3 py-2 text-sm font-mono text-gray-700 bg-gray-50 border border-gray-200 rounded-lg break-all">
                {derivedSlug || <span className="text-gray-400">à renseigner</span>}
              </p>
              <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1.5">
                <Wand2 size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  Composé par le modèle depuis{' '}
                  {citedFields.length > 1 ? 'les champs' : 'le champ'}{' '}
                  {citedFields.map((key, i) => (
                    <span key={key}>
                      {i > 0 ? ', ' : ''}<span className="font-mono">{key}</span>
                    </span>
                  ))}{' '}
                  — il suit donc leur valeur à chaque enregistrement. S&apos;il est déjà
                  pris par un autre contenu de ce type, le serveur y ajoute un suffixe.
                </span>
              </p>
            </div>
          ) : (
            <div>
              <label htmlFor={fieldId} className="block text-xs font-medium text-gray-500 mb-1.5">
                Segment d&apos;adresse
              </label>
              <input
                id={fieldId}
                ref={inputRef}
                type="text"
                value={draft}
                onChange={e => setDraft(normalize(e.target.value))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply() } }}
                placeholder={isNew ? 'auto-généré depuis le titre' : ''}
                aria-describedby={helpId}
                spellCheck={false}
                className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600"
              />
              <p id={helpId} className="text-xs text-gray-400 mt-1.5">
                Laissé vide, il est dérivé du titre
                {titleFallback ? <> (« {titleFallback} »)</> : null} à l&apos;enregistrement.
                S&apos;il est déjà pris par un autre contenu de ce type, le serveur y ajoute
                un suffixe et renvoie l&apos;adresse retenue.
              </p>
            </div>
          )}

          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/40">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
              Adresse publique
            </p>
            {preview ? (
              <>
                <p className="text-sm font-mono text-gray-700 break-all">{preview}</p>
                <p className="text-xs text-gray-500 mt-1.5">
                  Chemin donné par le modèle{modelTitle ? <> « {modelTitle} »</> : null}, commun à
                  tous les contenus de ce type — il se règle dans l&apos;onglet « Modèle » du type.
                  {siteName ? <> Servi sur le site {siteName}.</> : null}
                </p>
              </>
            ) : (
              <p className="text-xs text-gray-500">
                Ce type de contenu n&apos;a pas de chemin public : son modèle n&apos;en déclare
                pas (onglet « Modèle » du type). Le segment d&apos;adresse reste son identifiant.
              </p>
            )}
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {derived ? 'Fermer' : 'Annuler'}
          </button>
          {/* Rien à appliquer quand le segment est dérivé : il n'y a pas de
              saisie à reporter dans l'état de l'écran. */}
          {!derived && (
            <button
              type="button"
              onClick={apply}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors"
            >
              Appliquer
            </button>
          )}
        </footer>
      </div>
    </div>
  )
}

export default BuilderObjectAddressPanel
